import React from 'react';
import { styled } from '@this/constants/themes';
import { digits } from '@this/src/util';

import type HotelList from '@this/domain/hotel/hotel_list';
import type User from '@this/domain/user/user';
import type SearchQuery from '@this/domain/search_query';
import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import type SearchResultItem from '@this/src/domain/search_result_item';

import HotelMap from '@this/shared/hotel_map/hotel_map';
import SelectListAreaSearchTemplate from '../list_area_search/list_area_search.template';
import SelectHotelList from '../hotel_list/hotel_list';
import HotelScoresReason from '../hotel_scores_reason';

interface HotelListAreaProps {
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  serviceId: number;
  icon: string;
  listWrapper: HotelList;
  hotelPriceLimit: number | null;
  queryItem?: SearchQueryItem;
  resultItem?: SearchResultItem;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
  errors: string[];
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  me: User | null;
}

const HotelListArea: React.FC<HotelListAreaProps> = ({
  query,
  store,
  repository,
  serviceId,
  icon,
  listWrapper,
  hotelPriceLimit,
  queryItem,
  resultItem,
  showSearchBox,
  toggleShowSearchBox,
  errors,
  handleSubmit,
  me
}) => {
  return (
    <>
      {store.result.type === 'separate' &&
        (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
        store.result.items.length > 0 &&
        query &&
        queryItem && (
          <SelectListAreaSearchTemplate
            showSearchBox={showSearchBox}
            toggleShowSearchBox={toggleShowSearchBox}
            handleSubmit={handleSubmit}
            queryItem={queryItem}
            query={query}
            errors={errors}
          />
        )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: '10px'
        }}
      >
        <span>
          <StyledIcon>{icon}</StyledIcon>
          <StyledTitle>
            <span>すべての候補(</span>
            <span data-wovn-ignore>{listWrapper.length()}</span>
            <span>件)</span>
          </StyledTitle>
          {hotelPriceLimit && hotelPriceLimit > 0 ? (
            <StyledNote>
              <span>※規程金額：</span>
              <span data-wovn-ignore>{digits(hotelPriceLimit)}</span>
              <span>円</span>
            </StyledNote>
          ) : null}
        </span>
        <StyledToggleButton onClick={listWrapper.handleToggleMap}>
          {listWrapper.showMap ? 'リストを表示' : '地図を表示'}
        </StyledToggleButton>
      </div>
      {listWrapper.showMap === false && (
        <StyledSortTabs>
          <StyledSortTab
            active={listWrapper.sortKey === 'orderedIndex'}
            onClick={listWrapper.handleSortKeyChange('orderedIndex')}
          >
            おすすめ順
          </StyledSortTab>
          <StyledSortTab
            active={listWrapper.sortKey === 'average_price_with_tax'}
            onClick={listWrapper.handleSortKeyChange('average_price_with_tax')}
          >
            料金が安い順
          </StyledSortTab>
          <StyledSortTab
            active={listWrapper.sortKey === 'distance'}
            onClick={listWrapper.handleSortKeyChange('distance')}
          >
            目的地に近い順
          </StyledSortTab>
          <StyledSortTab
            active={listWrapper.sortKey === 'station_distance'}
            onClick={listWrapper.handleSortKeyChange('station_distance')}
          >
            最寄り駅に近い順
          </StyledSortTab>
        </StyledSortTabs>
      )}
      {resultItem?.rakutenStaticFileUpdatedAt && (
        <StyledStaticFileUpdatedAtText>
          在庫データ：{resultItem.rakutenStaticFileUpdatedAt.format('YYYY年MM月DD日 HH時mm分')}更新
        </StyledStaticFileUpdatedAtText>
      )}
      <StyledBody>
        {listWrapper.showMap ? (
          <div>
            <HotelMap
              hotels={listWrapper}
              destLocation={resultItem ? resultItem.destLocation : null}
              height="66vh"
              serviceId={serviceId}
              hotelPriceLimit={hotelPriceLimit}
              searchQueryId={store.result.searchQueryId}
              onHotelSelect={(i: number) => listWrapper.selectByIndex(i)}
              onChange={store.setTab('none')}
              repository={store.result.type === 'separate' ? repository : undefined}
            />
          </div>
        ) : (
          <>
            {listWrapper.sortKey === 'orderedIndex' && (
              <HotelScoresReason
                searchQueryItem={resultItem?.queryItem}
                reasonStatus={resultItem?.reasonStatus || 'none'}
                scoreReason={resultItem?.scoreReason || null}
              />
            )}
            <SelectHotelList
              hotels={listWrapper}
              hotelPriceLimit={hotelPriceLimit}
              searchQueryId={store.result.searchQueryId}
              onChange={store.setTab('none')}
              user={store.user}
              me={me}
              destLocation={resultItem ? resultItem.destLocation : null}
            />
          </>
        )}
      </StyledBody>
    </>
  );
};

const StyledIcon = styled.div`
  display: inline-block;
  text-align: center;
  width: 20px;
  height: 20px;
  background: ${props => props.theme.iconColor};
  border-radius: 10px;
  color: white;
  font-weight: bold;
  margin-right: 10px;
`;

const StyledTitle = styled.div`
  display: inline-block;
  font-weight: bold;
  margin-right: 10px;
`;

const StyledNote = styled.div`
  display: inline-block;
`;

const StyledSortTabs = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledSortTab = styled.div<{ active: boolean }>`
  width: calc((100% - 5px * 3) / 4);
  cursor: pointer;
  margin-bottom: 15px;
  padding-bottom: 3px;
  font-size: 10px;
  font-weight: bold;
  border-bottom: 2px solid ${({ theme, active }) => (active ? theme.linkColor : theme.grayTextColor)};
  color: ${({ theme, active }) => (active ? theme.linkColor : theme.grayTextColor)};

  &:hover {
    border-color: ${props => props.theme.linkColor};
    color: ${props => props.theme.linkColor};
  }
`;

const StyledStaticFileUpdatedAtText = styled.p`
  width: 100%;
  text-align: right;
  font-size: 10px;
  color: ${props => props.theme.grayTextColor};
  margin-bottom: 15px;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;

  max-height: 66vh;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const StyledToggleButton = styled.button`
  all: unset;
  display: inline-block;
  background: transparent !important;
  border: none !important;
  color: ${props => props.theme.linkColor} !important;
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;
  font-family: inherit;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: none !important;
  outline: none !important;

  &:hover {
    background-color: ${props => props.theme.accentColorLight || '#f5f5f5'} !important;
    color: ${props => props.theme.linkColor} !important;
    border: none !important;
    box-shadow: none !important;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.linkColor} !important;
    outline-offset: 2px;
    color: ${props => props.theme.linkColor} !important;
  }

  &:active {
    background-color: ${props => props.theme.accentColorLight || '#f5f5f5'} !important;
    color: ${props => props.theme.linkColor} !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

export default HotelListArea;
